import type { InfiniteData } from '@tanstack/react-query';
import type { MessagesPage } from './api';
import { carimboDaPromocao, maisNovaDoCache } from './carimbo-da-bolha';
import { montaBolhaDoEvento } from './mensagem-do-evento';
import type { TicketStatus } from './types';

/**
 * Payload do evento `message.received` (Socket.IO). Extraído do hooks.ts
 * junto com `aplicaMensagem` pra ser usável FORA da closure dos listeners da
 * AtendimentoPage — o handler global (useDesktopNotifications) encaixa a
 * mensagem com a MESMA máquina quando a página está desmontada (antes ele só
 * invalidava, e abrir a conversa pintava o cache velho + refetch = "montagem"
 * visível, medida no rastro 2026-07-24).
 */
export interface InboundEvent {
  tenant_id: number;
  ticket_id: number;
  message_id: number | null;
  type: string;
  body: string | null;
  media_id?: number;
  /** Inbound vem 'pending' (worker baixa); outbound vem 'stored' (já uploaded). */
  media_status?: 'pending' | 'stored' | 'failed';
  media_original_name?: string;
  /** Mime type pra renderização (`audio/ogg`, `image/jpeg`, etc).
   *  Sem isso, AudioPlayer/MediaContent caem no fallback "[audio]/[image]"
   *  no realtime emit — só renderiza certo depois de F5/refetch. */
  media_mime?: string;
  /** Duração em ms (áudio/vídeo). AudioPlayer mostra timer sem precisar
   *  baixar o arquivo. */
  media_duration_ms?: number;
  /** Waveform pré-calculada (32 floats 0..1). Sem isso, AudioPlayer mostra
   *  pattern neutro até user dar play. */
  media_waveform?: number[];
  /** Dimensões da imagem/vídeo (do protobuf WhatsApp). Frontend reserva
   *  espaço via aspect-ratio → bolha nasce com altura final. */
  media_width?: number;
  media_height?: number;
  /** URL HMAC pra thumb gerada server-side via ffmpeg (vídeo).
   *  Permite <video poster> imediato sem flash. */
  media_thumbnail_url?: string | null;
  // worker-meta-ingest emite `received_at` (inbound); api emite `created_at` (outbound).
  received_at?: string;
  created_at?: string;
  ticket_status?: TicketStatus;
  /** Dono do atendimento (null = na fila). Os workers de entrada mandam. */
  user_id?: number | null;
  direction?: 'inbound' | 'outbound';
  /** Worker-chatbot emite true ao enviar msg pelo bot — frontend usa pra
   *  flipar o ícone Bot no card sem precisar de refetch. */
  bot_active?: boolean;
  /** Reply/quote: wamid da msg citada (cliente respondeu uma anterior). */
  reply_to_wamid?: string | null;
  reply_to?: {
    wamid: string;
    body: string | null;
    type: string;
    direction: 'inbound' | 'outbound';
  } | null;
  /** Cliente respondeu a um status do dono do canal (badge especial). */
  status_reply?: {
    status_wamid?: string;
    status_type?: string;
    status_caption?: string;
    status_thumbnail_b64?: string;
  } | null;
  /** Mensagem encaminhada — rótulo "Encaminhada" na bolha. */
  forwarded?: boolean;
  frequently_forwarded?: boolean;
  /** Prévia estruturada do template HSM — vide EventoDeMensagem. */
  template_preview?: import('./types').ChatMessage['template_preview'];
  /** Nota interna (só no painel) — bolha amarela com autor. */
  internal_note?: boolean;
  sent_by_user_id?: number | null;
  sent_by_user_name?: string | null;
  /** Estado de entrega — vide `EventoDeMensagem` em `mensagem-do-evento.ts`. */
  status_rank?: number;
  sent_at?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  /** Saída paga (Cloud oficial num canal com o selo ligado) — bolha ao vivo. */
  billable?: boolean;
  /** Agrupador: mensagem virou pacote (2+ textos num envio só). */
  batched?: boolean;
  /** A IA reescreveu o pacote — o `body` do evento é o texto final que o
   *  cliente recebeu. Sem aplicar isso, o atendente continuaria vendo o que
   *  ele digitou enquanto o cliente leu outra coisa. */
  ai_rewritten?: boolean;
  /** O que o atendente escreveu antes da IA reescrever (hover do selo). Viaja
   *  no evento junto com `ai_rewritten`: sem ele o selo aparecia ao vivo mas
   *  sem conteúdo, e o original só surgia depois de um F5. */
  ai_original?: string | null;
  /** Coexistência: o worker marca o eco do celular do lojista com este source. */
  source?: string;
  /** Agrupador: prazo do pacote ainda aberto (ISO) — alimenta a contagem. */
  batch_open_until?: string | null;
  /** Agrupador inteligente: relógio parado neste restante (ms); null = contando. */
  batch_held_remaining_ms?: number | null;
}

/** Insere/promove a mensagem do evento nas páginas em cache. Puro. */
export function aplicaMensagem(
  prev: InfiniteData<MessagesPage, number | undefined>,
  evt: InboundEvent,
): InfiniteData<MessagesPage, number | undefined> {
  // Mensagem já em cache: NÃO é duplicata pura — mídia chega em DOIS
  // eventos (1º "recebida, baixando"; 2º "pronta", com id/mime/waveform/
  // dimensões). O 2º tem que atualizar a bolha, senão ela trava em
  // "baixando" e a imagem fica quebrada.
  //
  // Antes do encaixe isso não aparecia na conversa FECHADA: ela invalidava
  // e o refetch trazia o estado final do servidor. Com o encaixe, o 2º
  // evento é a ÚNICA chance de completar a bolha — o `onMediaReady` só
  // cobre a conversa aberta.
  if (
    evt.message_id !== null &&
    prev.pages.some((p) => p.messages.some((m) => m.id === evt.message_id))
  ) {
    const temNovidadeDeMidia =
      evt.media_id !== undefined ||
      evt.media_status !== undefined ||
      evt.media_mime !== undefined ||
      evt.media_waveform !== undefined ||
      evt.media_duration_ms !== undefined ||
      evt.media_thumbnail_url !== undefined;
    // Registro de SISTEMA morfa: "Cliente ligando…" é re-emitido com o MESMO
    // id e o body final ("Chamada de voz perdida/recusada/recebida") — o
    // patch do body é a única chance de atualizar o banner ao vivo.
    const temNovidadeDeSistema = evt.type === 'system' && typeof evt.body === 'string';
    // Selo "paga" (Cobrada): quando o POST promoveu a bolha ANTES do socket, ela
    // já está em cache com billable indefinido. O eco traz o billable do servidor
    // — sem aplicar aqui, o selo só aparecia no F5.
    const temNovidadeDeBillable = evt.billable === true;
    // Agrupador: a cada texto novo o servidor reemite a MESMA mensagem com o
    // corpo acumulado e um prazo novo. É a única chance de a bolha crescer ao
    // vivo (e a contagem reiniciar) sem F5 — o id já está em cache.
    // Saiu pelo celular (Digitador): o aviso chega DEPOIS da bolha existir,
    // quando o eco da coexistência casa com a mensagem. Sem tratar como
    // novidade, o selo do celular só aparecia no F5.
    const temNovidadeDeCelular = evt.source === 'smb_app';
    // `batch_open_until` tem TRÊS estados e os três importam: string = prazo
    // novo (a contagem reinicia), `null` = o pacote FECHOU (a contagem some), e
    // ausente = o evento não fala de pacote. Sem o caso `null` explícito, o
    // aviso de fechamento chegava e era ignorado — a contagem seguia correndo
    // até zerar sozinha, e quem clicou em "enviar agora" achava que falhou.
    const temNovidadeDePacote =
      evt.batched === true ||
      evt.ai_rewritten === true ||
      typeof evt.batch_open_until === 'string' ||
      evt.batch_open_until === null;
    // Pacote FECHOU com carimbo do envio: a mensagem agrupada passa a ter a
    // hora em que saiu (igual à sem agrupador) e desce pra baixo do que o
    // cliente mandou enquanto o relógio corria. Mesmo piso da promoção — a
    // bolha nunca fica antes de algo que já está abaixo dela.
    const temCarimboDoEnvio = evt.batch_open_until === null && typeof evt.created_at === 'string';
    // O piso conta só o que JÁ SAIU. Bolha provisória (id negativo, ainda sem a
    // hora do servidor) e pacote ainda contando não saíram antes deste — não
    // podem empurrá-lo pra baixo. Caso real: a resposta que corta o pacote (a
    // API grava o pacote 1 ms antes dela); a bolha da resposta já está na tela
    // quando o aviso de fechamento chega. Cobre também a aba com relógio
    // adiantado, em que a provisória nasce no futuro.
    //
    // Mensagem NOSSA com carimbo do servidor DEPOIS do pacote também não entra:
    // ela saiu depois dele. Caso real: a mídia que corta o pacote (a API grava o
    // pacote 1 ms antes dela) já ganhou o carimbo do servidor quando o aviso de
    // fechamento chega — com ela no piso, o texto descia pra baixo do áudio na
    // tela (o banco e o cliente tinham a ordem certa; o F5 corrigia). O piso
    // segue valendo pra mensagem do CLIENTE, cujo carimbo pode vir adiantado.
    const fechouEmMs = temCarimboDoEnvio ? Date.parse(evt.created_at as string) : Number.NaN;
    const saiuDepoisDoPacote = (m: { direction: string; created_at: string }): boolean =>
      m.direction === 'outbound' && Date.parse(m.created_at) > fechouEmMs;
    const maisNovaParaOEnvio = temCarimboDoEnvio
      ? (maisNovaDoCache(
          prev.pages.map((p) => ({
            messages: p.messages.filter(
              (m) => m.id > 0 && !m.batch_open_until && !saiuDepoisDoPacote(m),
            ),
          })),
          new Set([evt.message_id]),
        )?.created_at ?? null)
      : null;
    if (
      !temNovidadeDeMidia &&
      !temNovidadeDeSistema &&
      !temNovidadeDeBillable &&
      !temNovidadeDePacote &&
      !temNovidadeDeCelular
    )
      return prev;
    return {
      ...prev,
      pages: prev.pages.map((p) => ({
        ...p,
        messages: p.messages.map((m) =>
          m.id === evt.message_id
            ? {
                ...m,
                ...(temNovidadeDeSistema && { body: evt.body }),
                ...(temNovidadeDeBillable && { billable: true }),
                // Saiu pelo aparelho ⇒ não passou pela Cloud ⇒ não custou. Apagar
                // o selo aqui conserta a bolha que nasceu marcada antes de a rota
                // ser decidida (pacote aberto num canal com Digitador).
                ...(temNovidadeDeCelular && { from_phone: true, billable: false }),
                ...(temNovidadeDePacote && {
                  ...(typeof evt.body === 'string' && { body: evt.body }),
                  ...(temCarimboDoEnvio && {
                    created_at: carimboDaPromocao(m.created_at, evt.created_at, maisNovaParaOEnvio),
                  }),
                  ...(evt.batched === true && { batched: true }),
                  ...(evt.ai_rewritten === true && {
                    ai_rewritten: true,
                    ...(typeof evt.ai_original === 'string' && { ai_original: evt.ai_original }),
                  }),
                  batch_open_until: evt.batch_open_until ?? null,
                  ...(evt.batch_held_remaining_ms !== undefined && {
                    batch_held_remaining_ms: evt.batch_held_remaining_ms,
                  }),
                }),
                ...(evt.media_id !== undefined && { media_id: evt.media_id }),
                ...(evt.media_status !== undefined && { media_status: evt.media_status }),
                ...(evt.media_mime !== undefined && { media_mime: evt.media_mime }),
                ...(evt.media_waveform !== undefined && { media_waveform: evt.media_waveform }),
                ...(evt.media_duration_ms !== undefined && {
                  media_duration_ms: evt.media_duration_ms,
                }),
                ...(evt.media_width !== undefined && { media_width: evt.media_width }),
                ...(evt.media_height !== undefined && { media_height: evt.media_height }),
                ...(evt.media_thumbnail_url !== undefined && {
                  media_thumbnail_url: evt.media_thumbnail_url,
                }),
                ...(evt.media_original_name !== undefined && {
                  media_original_name: evt.media_original_name,
                }),
              }
            : m,
        ),
      })),
    };
  }
  // Dedup OUTBOUND contra optimistic (id negativo): se o socket emit
  // chegou ANTES do onSuccess do mutation linkTempToReal, a bolha
  // optimistic ainda tem tempId<0. Promove ela ao realId em vez de
  // inserir uma nova (evita duplicação visível pro usuário).
  if (evt.direction === 'outbound' && evt.message_id !== null) {
    let foundTempId: number | null = null;
    for (const p of prev.pages) {
      for (const m of p.messages) {
        if (m.id < 0 && m.direction === 'outbound' && m.body === evt.body) {
          foundTempId = m.id;
          break;
        }
      }
      if (foundTempId !== null) break;
    }
    if (foundTempId !== null) {
      // Âncora do piso: msg mais nova excluindo a própria temp/real.
      const maisNova =
        maisNovaDoCache(prev.pages, new Set([foundTempId, evt.message_id]))?.created_at ?? null;
      return {
        ...prev,
        pages: prev.pages.map((p) => ({
          ...p,
          messages: p.messages.map((m) =>
            m.id === foundTempId
              ? {
                  ...m,
                  id: evt.message_id as number,
                  // Promoção adota o carimbo do servidor com piso — cobre
                  // aba atrasada E adiantada (2 casos medidos em 2026-07-24).
                  created_at: carimboDaPromocao(m.created_at, evt.created_at, maisNova),
                  // Selo "paga" vem do servidor no evento — a bolha otimista
                  // não sabia disso; sem copiar aqui, só aparecia no F5.
                  billable: evt.billable ?? m.billable ?? false,
                  // Idem para o pacote: promover sem o prazo apagava a contagem
                  // de uma bolha que estava contando certo.
                  ...(evt.batch_open_until !== undefined && {
                    batch_open_until: evt.batch_open_until,
                  }),
                  // Prazo novo = texto entrou no pacote = a caixa esvaziou:
                  // o relógio destrava (o servidor manda null junto).
                  ...(evt.batch_held_remaining_ms !== undefined && {
                    batch_held_remaining_ms: evt.batch_held_remaining_ms,
                  }),
                  ...(evt.batched === true && { batched: true }),
                  ...(evt.ai_rewritten === true && {
                    ai_rewritten: true,
                    ...(typeof evt.ai_original === 'string' && { ai_original: evt.ai_original }),
                  }),
                }
              : m,
          ),
        })),
      };
    }
  }
  const optimistic = montaBolhaDoEvento(evt);
  // Append na primeira página (batch mais recente).
  const [firstPage, ...rest] = prev.pages;
  if (!firstPage) return prev;
  return {
    ...prev,
    pages: [{ ...firstPage, messages: [...firstPage.messages, optimistic] }, ...rest],
  };
}
